import { useAsyncEffect } from '@xylabs/sdk-react'
import { AuctionStatus, Quadkey } from '@xyo-network/sdk-xyoworld-js'
import BigNumber from 'bn.js'
import { PropsWithChildren, useState } from 'react'

import { parseBigNumber } from '../../parseBigNumber'
import { useMinter, useRefreshMinter, useUpdateMinter } from '../Minter'
import { StatsContext } from './Context'

interface Props {
  enabled?: boolean
}

export const StatsLoader: React.FC<PropsWithChildren<Props>> = (props) => {
  const enabled = true
  const { minter } = useMinter()
  const [loadCount, setLoadCount] = useState(0) //used to trigger effects

  const [geotokensMinted, setGeotokensMinted] = useState<number>()
  const [totalBids, setTotalBids] = useState<number>()
  const [totalBidders, setTotalBidders] = useState<number>()
  const [totalXyoBid, setTotalXyoBid] = useState<BigNumber>()
  const [totalXyoBurned, setTotalXyoBurned] = useState<BigNumber>()
  const [totalXyoFlowed, setTotalXyoFlowed] = useState<BigNumber>()
  const [totalXyoSpent, setTotalXyoSpent] = useState<BigNumber>()
  const [totalXyoMintingRewards, setTotalXyoMintingRewards] = useState<BigNumber>()

  const updateMinter = useUpdateMinter()
  const refreshMinter = useRefreshMinter()

  useAsyncEffect(
    // eslint-disable-next-line react-hooks/exhaustive-deps
    async (mounted) => {
      if (minter && enabled) {
        const geotokensMinted = (await minter.listAuctions(AuctionStatus.Minted))?.length
        if (mounted()) {
          setGeotokensMinted(geotokensMinted)
        }
      }
    },
    [minter, loadCount, enabled],
  )

  useAsyncEffect(
    // eslint-disable-next-line react-hooks/exhaustive-deps
    async (mounted) => {
      if (minter && enabled) {
        const events = await minter.events().bidPlaced()

        const bids = events?.length
        let totalXyo = new BigNumber(0)
        const bidders = new Set<string>()
        const highBid = new Map<string, BigNumber>()
        for (const event of events) {
          const quadkey = Quadkey.fromBase10String(event.args.id.toString())
          const amount = parseBigNumber(event.args.amount)
          const by = parseBigNumber(event.args.by)
          totalXyo = totalXyo.add(parseBigNumber(event.args.amount))
          const currentBid = highBid.get(quadkey.toBase4HashLabel()) ?? new BigNumber(0)
          if (amount.gt(currentBid)) {
            highBid.set(quadkey.toBase4HashLabel(), amount)
          }
          bidders.add(by.toString('hex'))
        }
        let totalSpent = new BigNumber(0)
        for (const bid of highBid) {
          totalSpent = totalSpent.add(bid[1])
        }
        if (mounted()) {
          setTotalXyoSpent(totalSpent)
          setTotalBids(bids)
          setTotalXyoBid(totalXyo)
          setTotalBidders(bidders.size)
        }
      }
    },
    [minter, loadCount, enabled],
  )

  useAsyncEffect(
    // eslint-disable-next-line react-hooks/exhaustive-deps
    async (mounted) => {
      if (minter && enabled) {
        const events = await minter.events().tokensBurned()
        let totalXyo = new BigNumber(0)
        for (const event of events) {
          const amount = parseBigNumber(event.args.amount)
          totalXyo = totalXyo.add(amount)
        }
        if (mounted()) {
          setTotalXyoBurned(totalXyo)
        }
      }
    },
    [minter, loadCount, enabled],
  )

  useAsyncEffect(
    // eslint-disable-next-line react-hooks/exhaustive-deps
    async (mounted) => {
      if (minter && enabled) {
        const events = await minter.events().mintingFeeSent()
        let totalXyo = new BigNumber(0)
        for (const event of events) {
          const amount = parseBigNumber(event.args.amount)
          totalXyo = totalXyo.add(amount)
        }
        if (mounted()) {
          setTotalXyoMintingRewards(totalXyo)
        }
      }
    },
    [minter, loadCount, enabled],
  )

  useAsyncEffect(
    // eslint-disable-next-line react-hooks/exhaustive-deps
    async (mounted) => {
      if (minter && enabled) {
        const events = await minter.events().tokensFlowed()
        let totalXyo = new BigNumber(0)
        for (const event of events) {
          const amount = parseBigNumber(event.args.amount)
          totalXyo = totalXyo.add(amount)
        }
        if (mounted()) {
          setTotalXyoFlowed(totalXyo)
        }
      }
    },
    [minter, loadCount, enabled],
  )

  const clearStats = () => {
    setGeotokensMinted(undefined)
    setTotalBidders(undefined)
    setTotalBids(undefined)
    setTotalXyoBid(undefined)
    setTotalXyoBurned(undefined)
    setTotalXyoFlowed(undefined)
    setTotalXyoMintingRewards(undefined)
    setTotalXyoSpent(undefined)
  }

  const refresh = async () => {
    clearStats()
    await refreshMinter?.()
    setLoadCount(loadCount + 1)
  }

  const update = async () => {
    clearStats()
    await updateMinter?.()
    setLoadCount(loadCount + 1)
  }

  return (
    <StatsContext.Provider
      value={{
        geotokensMinted,
        refresh,
        totalBidders,
        totalBids,
        totalXyoBid,
        totalXyoBurned,
        totalXyoFlowed,
        totalXyoMintingRewards,
        totalXyoSpent,
        update,
      }}
    >
      {props.children}
    </StatsContext.Provider>
  )
}
